import PropTypes from 'prop-types'
import styled from 'styled-components'

import {
  PB40T,
  PB90T,
  PB97T,
  SLB,
  SLB90T,
  PG,
  PG90T,
  SO,
  SO90T,
  SLR,
  SLR90T,
  WHITE,
  PB
} from '../../colors'
import { base } from '../../mixins'

const fontSizes = {
  small: '10px',
  large: '14px'
}
const padding = {
  small: '2.5px 7px',
  large: '5.5px 15px'
}
const backgroundColors = {
  monochrome: WHITE,
  gray: PB97T,
  green: PG90T,
  blue: SLB90T,
  red: SLR90T,
  orange: SO90T
}
const fontColors = {
  monochrome: PB,
  gray: PB40T,
  green: PG,
  blue: SLB,
  red: SLR,
  orange: SO
}
const getFontColor = ({ color }) => fontColors[color]
const getPadding = ({ size }) => padding[size]
const getBorderColor = ({ color }) => (color === 'gray' ? PB90T : fontColors[color])
const getBackgroundColor = ({ color, variant }) => {
  if (variant === 'outlined') {
    return 'tranparent'
  }

  return backgroundColors[color]
}
const getHoverFontColor = ({ color, variant }) => {
  if (variant === 'outlined') {
    return WHITE
  }

  return fontColors[color]
}
const getHoverBackgroundColor = ({ color, variant }) => {
  if (variant === 'outlined') {
    return fontColors[color]
  }

  return backgroundColors[color]
}
const IconWrap = styled.span`
  display: inline-flex;
  margin: ${({ align }) => (align === 'left' ? '0 0 0 7.5px' : '0 7.5px 0 0')};
`
const StyledChip = styled.span`
  ${base}
  color: ${getFontColor};
  font-size: ${({ size }) => fontSizes[size]};
  display: inline-flex;
  flex-direction: ${({ align }) => (align === 'right' ? 'row-reverse' : 'row')};
  align-items: center;
  justify-content: space-between;
  white-space: nowrap;
  background-color: ${({ bgColor }) => bgColor ?? getBackgroundColor};
  border: 1px solid ${getBorderColor};
  border-radius: 25px;
  line-height: normal;
  padding: ${getPadding};

  &:hover {
    cursor: ${({ onClick }) => (onClick ? 'pointer' : 'auto')};
    background-color: ${({ hoverBgColor }) => hoverBgColor ?? getHoverBackgroundColor};
    color: ${getHoverFontColor};
  }
`
const renderBody = ({ align, children, icon: Icon }) => {
  return (
    <>
      <span>{children}</span>
      {Icon ? (
        <IconWrap align={align}>
          <Icon height="14px" width="14px" />
        </IconWrap>
      ) : null}
    </>
  )
}
/**
 * The Chip component.
 */
const Chip = ({
  children,
  size = 'small',
  align = 'right',
  color = 'monochrome',
  variant = 'contained',
  bgColor,
  hoverBgColor,
  icon = null,
  ...rest
}) => {
  return (
    <StyledChip
      size={size}
      align={align}
      color={color}
      variant={variant}
      bgColor={bgColor}
      hoverBgColor={hoverBgColor}
      {...rest}>
      {renderBody({ align, icon, children })}
    </StyledChip>
  )
}

Chip.propTypes = {
  /** Callback when the chip is clicked. */
  onClick: PropTypes.func,
  /** Determines the size of the chip. */
  size: PropTypes.oneOf(['small', 'large']),
  /** Sets the text alignment. */
  align: PropTypes.oneOf(['left', 'right']),
  /** Sets the predefined styling applied to the chip. */
  color: PropTypes.oneOf(['monochrome', 'gray', 'green', 'blue', 'red', 'orange']),
  /** Overrides predefined styling for background color. */
  bgColor: PropTypes.string,
  /** Overrides predefined styling for hover background color. */
  hoverBgColor: PropTypes.string,
  /** Which style variant to use. */
  variant: PropTypes.oneOf(['contained', 'outlined']),
  /** Displays an icon inside the chip. */
  icon: PropTypes.elementType
}

export { Chip }
